<template>
  <article class="news-list-card">
    <a
      class="news-list-card__media"
      :href="detailHref"
      :aria-label="`${t('news.readArticle')} ${item.title}`"
      @click.prevent="viewDetail"
    >
      <img
        v-if="hasUsableImage"
        :src="imagePath"
        :alt="item.title"
        loading="lazy"
        decoding="async"
        @error="imageFailed = true"
      >
      <span v-else class="news-list-card__placeholder" aria-hidden="true">
        <img src="/static/images/newsroom-v2/image-placeholder.png" alt="">
      </span>
    </a>

    <div class="news-list-card__content">
      <h2>
        <a :href="detailHref" @click.prevent="viewDetail">{{ item.title }}</a>
      </h2>
      <p class="news-list-card__meta">{{ formattedMeta }}</p>
      <p class="news-list-card__summary">{{ summary }}</p>
      <a class="news-list-card__readmore" :href="detailHref" @click.prevent="viewDetail">
        {{ t('actions.continueReading') }}
      </a>
    </div>
  </article>
</template>

<script>
import { imgPath } from '@/utils/main'

const {
  buildNewsDetailPath,
  shouldUseNewsPlaceholder
} = require('@/utils/news-list.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')

export default {
  name: 'NewsListCard',
  mixins: [siteLanguageMixin],
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      imageFailed: false
    }
  },
  computed: {
    detailHref() {
      return buildNewsDetailPath(this.item.urlSlug)
    },
    imagePath() {
      return imgPath(this.item.coverImage)
    },
    hasUsableImage() {
      return !shouldUseNewsPlaceholder(this.item.coverImage, this.imageFailed)
    },
    summary() {
      return this.item.summary || ''
    },
    formattedMeta() {
      const value = this.item.publishTime || this.item.gmtCreate
      if (!value) return ''
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(this.currentLanguage === 'ZH' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  methods: {
    viewDetail() {
      if (!this.item.urlSlug) return

      const url = this.$router.resolve(this.detailHref).href
      window.open(url, '_blank', 'noopener')
    }
  }
}
</script>

<style scoped>
.news-list-card {
  display: grid;
  grid-template-columns: minmax(280px, 408px) minmax(0, 1fr);
  gap: 30px;
  min-height: 230px;
}

.news-list-card__media {
  position: relative;
  display: block;
  height: 230px;
  overflow: hidden;
  background: #f5f5f7;
}

.news-list-card__media > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.news-list-card__media:hover > img,
.news-list-card__media:focus-visible > img {
  transform: scale(1.02);
}

.news-list-card__placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: #f5f5f7;
}

.news-list-card__placeholder img {
  width: 54px;
  height: 45px;
  opacity: 0.2;
}

.news-list-card__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.news-list-card h2,
.news-list-card__meta,
.news-list-card__summary {
  margin: 0;
}

.news-list-card h2 {
  min-width: 0;
  max-width: 100%;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.news-list-card h2 a {
  display: block;
  width: 100%;
  max-width: 100%;
  color: #000;
  text-decoration: none;
  transition: color 0.2s ease;
  overflow-wrap: anywhere;
  word-break: normal;
}

.news-list-card h2 a:hover {
  color: #ef0a6a;
}

.news-list-card__meta {
  margin-top: 9px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  line-height: 1.4;
}

.news-list-card__summary {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 20px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 13px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
}

.news-list-card__readmore {
  align-self: flex-start;
  margin-top: auto;
  padding-top: 18px;
  color: #ef0a6a;
  font-size: 12px;
  line-height: 1.4;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.news-list-card__readmore:hover {
  color: #c90051;
}

.news-list-card__media:focus-visible,
.news-list-card h2 a:focus-visible,
.news-list-card__readmore:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

@media (max-width: 700px) {
  .news-list-card {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .news-list-card__media {
    width: 100%;
    height: auto;
    aspect-ratio: 400 / 230;
  }

  .news-list-card__summary {
    -webkit-line-clamp: 4;
  }

  .news-list-card__readmore {
    margin-top: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .news-list-card__media > img {
    transition: none;
  }
}
</style>
